import { useEffect, useState, type ReactNode } from 'react';
import { localeUI } from '../lib/format';
import { AlertTriangle, Bot, Check, Eye, EyeOff, FileText, Link2, RotateCcw } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';

/**
 * Validación del contrato firmado en Claim Review: muestra el contrato (link al PDF) y
 * las ALERTAS del AI — que el nombre coincida con el homeowner, la dirección, la
 * aseguradora, el número de póliza; y la fecha de firma + qué tan lejos está de hoy.
 */
type Check = { field: string; label: string; contractValue: string | null; claimValue: string | null; status: 'match' | 'mismatch' | 'missing'; note: string };
type Validation = { hasContract: boolean; signedAt: string | null; daysSinceSigned: number | null; checks: Check[]; summary: string | null; ai: boolean; needsAnalyze?: boolean; needsValidate?: boolean; analyzedFromFile?: boolean; validatedAt?: string | null };
type Contract = { signed_url: string | null; filled_url: string | null } | null;

const BADGE: Record<Check['status'], { bg: string; fg: string; icon: ReactNode }> = {
  match: { bg: 'var(--green-soft)', fg: 'var(--green)', icon: <Check size={12} strokeWidth={2} style={{ verticalAlign: 'text-bottom' }} /> },
  mismatch: { bg: 'var(--red-soft)', fg: 'var(--red)', icon: <AlertTriangle size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> },
  missing: { bg: 'var(--orange-soft)', fg: 'var(--orange)', icon: '?' },
};

export function ContractValidation({ claimId }: { claimId: string }) {
  const { t } = useI18n();
  const [val, setVal] = useState<Validation | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [showPdf, setShowPdf] = useState(false);
  const [plegado, setPlegado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Corre la comparación AI completa (contra los valores del e-sign).
  function run() {
    setLoading(true);
    api.get<Validation>(`/api/claims/${claimId}/contract/validation`).then(setVal)
      .catch(() => setVal({ hasContract: false, signedAt: null, daysSinceSigned: null, checks: [], summary: null, ai: false }))
      .finally(() => { setLoading(false); setLoaded(true); });
  }
  // Lee el PDF del contrato/LOR de Files con AI, extrae los datos y valida vs el claim.
  // Tras analizar, refresca el link del contrato (ahora enlazado al PDF del LOR).
  function analyze() {
    setAnalyzing(true);
    api.post<Validation>(`/api/claims/${claimId}/contract/analyze-file`, {}).then((v) => {
      setVal(v);
      api.get<{ contract: Contract }>(`/api/claims/${claimId}/contract`).then((r) => setUrl(r.contract?.signed_url || r.contract?.filled_url || null)).catch(() => {});
    }).catch(() => {})
      .finally(() => { setAnalyzing(false); setLoaded(true); });
  }
  // Al abrir: link del contrato + peek de la validación (muestra el análisis cacheado sin gastar tokens).
  useEffect(() => {
    api.get<{ contract: Contract }>(`/api/claims/${claimId}/contract`).then((r) => setUrl(r.contract?.signed_url || r.contract?.filled_url || null)).catch(() => {});
    api.get<Validation>(`/api/claims/${claimId}/contract/validation?peek=1`).then(setVal).catch(() => {}).finally(() => setLoaded(true));
  }, [claimId]);

  // Fecha-only o medianoche UTC → fijar a mediodía local para NO correrla un día por timezone
  // (arregla también contratos ya analizados antes del fix del backend).
  const fmt = (iso: string) => {
    const s = String(iso);
    const dOnly = /^\d{4}-\d{2}-\d{2}(?:T00:00:00)?/.test(s) ? s.slice(0, 10) : null;
    const d = dOnly ? new Date(`${dOnly}T12:00:00`) : new Date(s);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(localeUI());
  };
  const anyIssue = val?.checks.some((c) => c.status !== 'match');
  const showChecks = !!val?.hasContract && (val.checks.length > 0);
  const busy = loading || analyzing;

  /* LADO A LADO. Con el visor abierto el PDF medía 560px de alto y EMPUJABA las validaciones
     abajo: había que scrollear del contrato a los cuadritos y volver, que es justo lo que se
     está comparando. Ahora el LOR va a la izquierda y todo lo demás a la derecha, en paralelo.
     `sticky` mantiene el PDF a la vista mientras se recorre la lista de checks. */
  const cuerpo = (
    <>
      {!loaded && !val && <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}>{t('cv.hint')}</div>}

      {loaded && val && !val.hasContract && <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}>{t('cv.noContract')}</div>}

      {loaded && val?.needsAnalyze && <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}>{t('cv.analyzeHint')}</div>}
      {loaded && val?.needsValidate && <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}>{t('cv.hint')}</div>}

      {showChecks && val && (
        <div style={{ display: 'grid', gap: 8 }}>
          {val.analyzedFromFile && <div className="badge" style={{ fontSize: 'var(--fs-2xs)', background: '#e0f2fe', color: '#075985', width: 'fit-content' }}><Bot size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cv.fromFile')}</div>}
          {/* Fecha de firma + qué tan lejos de hoy. */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 'var(--fs-sm)' }}>
            <span className="muted">{t('cv.signed')}:</span>
            <b>{val.signedAt ? fmt(val.signedAt) : '—'}</b>
            {val.daysSinceSigned != null && (
              <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: val.daysSinceSigned > 30 ? 'var(--orange-soft)' : '#e0f2fe', color: val.daysSinceSigned > 30 ? 'var(--orange)' : '#075985' }}>
                {t('cv.daysAgo', { d: String(val.daysSinceSigned) })}
              </span>
            )}
          </div>
          {val.summary && <div style={{ fontSize: 'var(--fs-xs)', background: anyIssue ? 'var(--red-soft)' : 'var(--surface-2)', borderRadius: 8, padding: '6px 10px' }}>{val.summary}</div>}
          <div style={{ display: 'grid', gap: 5 }}>
            {val.checks.map((c) => {
              const b = BADGE[c.status];
              return (
                <div key={c.field} style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 'var(--fs-xs)', borderBottom: '1px solid var(--line)', paddingBottom: 5 }}>
                  <span className="badge" style={{ background: b.bg, color: b.fg, fontSize: 'var(--fs-2xs)' }}>{b.icon} {c.label}</span>
                  <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{c.note}</span>
                  {c.status !== 'match' && (c.contractValue || c.claimValue) && (
                    <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>· contrato: <b>{c.contractValue || '—'}</b> · claim: <b>{c.claimValue || '—'}</b></span>
                  )}
                </div>
              );
            })}
          </div>
          {!val.ai && <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('cv.noAi')}</div>}
        </div>
      )}
    </>
  );

  return (
    <div className="card" style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {/* El título pliega el módulo entero (pedido: "this is too much"). */}
        <span onClick={() => setPlegado((v) => !v)} title={plegado ? t('cv.expand') : t('cv.collapse')}
          className="muted" style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4, cursor: 'pointer' }}>
          <span style={{ fontSize: 'var(--fs-2xs)' }}>{plegado ? '▸' : '▾'}</span> <FileText size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cv.title')}
        </span>
        {!plegado && url && <button type="button" className="btn ghost sm" onClick={() => setShowPdf((v) => !v)}>{showPdf ? <><EyeOff size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cv.hidePdf')}</> : <><Eye size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cv.viewPdf')}</>}</button>}
        {!plegado && url && <a href={url} target="_blank" rel="noopener noreferrer" className="btn ghost sm"><Link2 size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cv.open')}</a>}
        <span style={{ flex: 1 }} />
        {/* PDF en Files sin analizar → Analyze; e-sign sin validar o re-análisis → Validate. */}
        {plegado ? null : val?.needsAnalyze
          ? <button className="btn sm" disabled={busy} onClick={analyze}>{analyzing ? '…' : <><Bot size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cv.analyze')}</>}</button>
          : val?.needsValidate
            ? <button className="btn sm" disabled={busy} onClick={run}>{loading ? '…' : <><Bot size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cv.validate')}</>}</button>
            : showChecks
              ? <button className="btn ghost sm" disabled={busy} onClick={val?.analyzedFromFile ? analyze : run}>{busy ? '…' : <><RotateCcw size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cv.reanalyze')}</>}</button>
              : <button className="btn sm" disabled={busy} onClick={run}>{loading ? '…' : <><Bot size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cv.validate')}</>}</button>}
      </div>

      {plegado ? null : showPdf && url ? (
        // LOR a la IZQUIERDA, validaciones a la DERECHA. minmax(0,…) en las dos columnas: sin eso
        // el iframe impone su ancho intrínseco y desborda la tarjeta en pantallas angostas.
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 10, alignItems: 'start' }}>
          <div style={{ minWidth: 0, position: 'sticky', top: 8 }}>
            <iframe title="contract" src={url} style={{ width: '100%', height: 560, border: '1px solid var(--line)', borderRadius: 8, background: 'var(--card)' }} />
          </div>
          <div style={{ minWidth: 0, display: 'grid', gap: 10, maxHeight: 560, overflowY: 'auto' }}>{cuerpo}</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>{cuerpo}</div>
      )}
    </div>
  );
}
